import { api, subirArchivo } from "@/lib/api"

/**
 * En que punto esta el traspaso del historico.
 *
 *   GET /api/onboarding/pasos ?tenant_id
 *
 * El backend devuelve HECHOS derivados de los datos, no instrucciones: si el
 * sistema no lo ve, no esta hecho, y ningun paso se puede marcar a mano. Que
 * hacer con cada hecho se explica en la pantalla, que es donde se puede escribir
 * para una persona.
 */

export type EstadoPaso = "pendiente" | "en_curso" | "listo" | "atencion"

export interface PasoTraspaso {
  clave: string
  estado: EstadoPaso
  evidencia: Record<string, unknown>
  /** Lo que el sistema SABE. Puede faltar; la instruccion no vive aqui. */
  detalle: string | null
  bloquea: string[]
  /**
   * Quien decidio dejarlo para despues, y por que.
   *
   * NO cambia `estado`: un paso pospuesto sigue diciendo la verdad sobre los
   * datos. Lo unico que cambia es que la pantalla lo saca del camino critico.
   * Un «skip» que pusiera el paso en verde seria la checklist manual que este
   * modulo existe para no ser.
   */
  pospuesto: { motivo: string; desde: string; por: string | null } | null
}

export interface PasosTraspaso {
  pasos: PasoTraspaso[]
  listos: number
  /** Ni listos ni pospuestos: lo que de verdad reclama atencion hoy. */
  pendientes_activos: number
  pospuestos: number
  total: number
  nota: string
}

export function fetchPasos(): Promise<PasosTraspaso> {
  return api.get<PasosTraspaso>("/onboarding/pasos")
}

export function posponerPaso(clave: string, motivo: string): Promise<{
  clave: string
  pospuesto: boolean
  aviso: string
}> {
  return api.post(`/onboarding/pasos/${clave}/posponer`, { motivo })
}

export function retomarPaso(clave: string): Promise<{ clave: string }> {
  return api.delete(`/onboarding/pasos/${clave}/posponer`)
}

/* ==========================================================================
 * De donde vienen los documentos
 * ========================================================================== */

export interface FuenteDocumental {
  id: string
  tipo: "drive" | "carga_manual" | "correo"
  referencia: string | null
  etiqueta: string | null
  notas: string | null
  verificado: boolean
  verificado_en: string | null
  verificacion_detalle: string | null
  entity_id: string | null
  razon_social: string | null
}

export function fetchFuentes(): Promise<{
  fuentes: FuenteDocumental[]
  /** Con QUIEN hay que compartir la carpeta. `null` = no hay cuenta de servicio. */
  correo_de_servicio: string | null
  nota: string
}> {
  return api.get("/onboarding/fuentes")
}

export function declararFuente(payload: {
  tipo: string
  referencia?: string | null
  etiqueta?: string | null
  entity_id?: string | null
  notas?: string | null
  motivo: string
}): Promise<{ id: string; tipo: string; referencia: string | null; siguiente: string | null }> {
  return api.post("/onboarding/fuentes", payload)
}

export function quitarFuente(id: string): Promise<{ id: string }> {
  return api.delete(`/onboarding/fuentes/${id}`)
}

/* ==========================================================================
 * Subir un comprobante desde los primeros pasos
 * ========================================================================== */

/**
 * `POST /ingest/upload` — el mismo endpoint que la cola de revisión.
 *
 * Vive aquí y no en `routes/review/api.ts` porque en los primeros pasos se usa
 * para OTRA cosa: allí se sube el comprobante que falta, aquí se vuelca el
 * histórico. La diferencia se nota en el resultado que interesa —allí el
 * `document_id` para abrirlo, aquí cuántos entraron y cuántos ya estaban— y por
 * eso el llamador de esta se queda con el recuento.
 *
 * `entity_id` es OBLIGATORIO y no lo pone ningún middleware: un comprobante
 * pertenece a una sociedad, no a un cliente. Sin sociedad activa no se puede
 * subir, y decirlo antes de mandar veinte megas es mejor que el 422 de después.
 */
export interface ResultadoSubida {
  raw_document_id: string | null
  duplicado: boolean
  document_id?: string | null
  normalizado?: boolean
  /** `comprobante` (XML → Revisión), `sustento` (PDF) o `sin_interpretar`. */
  tipo?: "comprobante" | "sustento" | "sin_interpretar"
  /** Un PDF que llegó sin su XML: queda en Revisión › «Llegaron sin comprobante fiscal». */
  sin_comprobante?: boolean
  /** Sólo si se subió un ZIP: el recuento de lo que traía dentro. */
  lote?: {
    comprobantes: number
    sustentos: number
    sin_comprobante: number
    sin_interpretar: number
    duplicados: number
    descartados: Array<{ nombre: string; motivo: string }>
  }
  mensaje: string
}

export function subirComprobante(archivo: File, entityId: string): Promise<ResultadoSubida> {
  const form = new FormData()
  form.append("archivo", archivo)
  form.append("motivo", "Volcado del histórico desde primeros pasos.")
  return subirArchivo<ResultadoSubida>("/ingest/upload", form, {
    query: { entity_id: entityId },
  })
}
